import { CopyButton } from "../../../../../_components/ui";
import "./_api-link-example-panel.scss";

const SAMPLE_PAYLOAD = {
  BUID: "billing_unit_001",
  CustomerId: "customer_12345",
  count: 150,
};

/**
 * Static informational panel shown when an "API LINK"-origin usage source
 * is selected - documents the webhook endpoint + payload shape a customer
 * would POST usage events to. Extracted from ~180 lines that lived inline
 * in the old modal's JSX.
 *
 * Deliberately generic, not per-record: confirmed by reading the old
 * code that neither the curl command nor the payload example ever
 * referenced `rowData`/`formData` for the actual values - every
 * API-LINK/Get-API-Link source shows the identical sample. Not a bug,
 * see docs/bug-sheet.md.
 */
const ApiLinkExamplePanel = () => {
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/webhook/userbillingdata`;

  // Timestamp must be fresh at copy time, not render time - a function
  // passed as `getValue` (rather than a precomputed `value`) so CopyButton
  // builds the curl command on click.
  const buildCurlCommand = () =>
    `curl -X POST ${webhookUrl} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_AUTH_TOKEN" \\
  -d '${JSON.stringify({ ...SAMPLE_PAYLOAD, timestamp: new Date().toISOString() }, null, 2)}'`;

  return (
    <div className="api-link-example-panel">
      <div className="api-link-example-panel__header">
        <h4 className="api-link-example-panel__title">Event example</h4>
        <CopyButton
          variant="contained"
          size="sm"
          label="Copy curl"
          getValue={buildCurlCommand}
          successMessage="Curl command copied to clipboard!"
          errorMessage="Failed to copy to clipboard"
        />
      </div>

      <p className="api-link-example-panel__description">Use this endpoint to send usage data to your usage source:</p>

      <div className="api-link-example-panel__code-block">
        <div className="api-link-example-panel__code-line">
          curl -X POST <span className="api-link-example-panel__code-url">{webhookUrl}</span> \
        </div>
        <div className="api-link-example-panel__code-line api-link-example-panel__code-line--indent">-H "Content-Type: application/json" \</div>
        <div className="api-link-example-panel__code-line api-link-example-panel__code-line--indent">-H "Authorization: Bearer YOUR_AUTH_TOKEN" \</div>
        <div className="api-link-example-panel__code-line api-link-example-panel__code-line--indent">
          -d '{JSON.stringify({ ...SAMPLE_PAYLOAD, timestamp: new Date().toISOString() }, null, 2)}'
        </div>
      </div>

      <div className="api-link-example-panel__token-info">
        <p>
          <strong>Authorization header:</strong> include your authentication token in the request headers. Replace{" "}
          <code>YOUR_AUTH_TOKEN</code> with your actual authentication token.
        </p>
      </div>

      <div className="api-link-example-panel__fields">
        <h5>Required data structure</h5>
        <div className="api-link-example-panel__field-row">
          <span className="api-link-example-panel__field-name">BUID</span>
          <span className="api-link-example-panel__field-desc">Billing Unit ID (string)</span>
        </div>
        <div className="api-link-example-panel__field-row">
          <span className="api-link-example-panel__field-name">CustomerId</span>
          <span className="api-link-example-panel__field-desc">Customer identifier (string)</span>
        </div>
        <div className="api-link-example-panel__field-row">
          <span className="api-link-example-panel__field-name">count</span>
          <span className="api-link-example-panel__field-desc">Usage count/quantity (number)</span>
        </div>
        <div className="api-link-example-panel__field-row">
          <span className="api-link-example-panel__field-name">timestamp</span>
          <span className="api-link-example-panel__field-desc">Event timestamp (ISO 8601 string)</span>
        </div>
      </div>

      <p className="api-link-example-panel__note">
        This is the actual webhook endpoint URL for your usage source. Remember to include your authentication token in the Authorization header.
      </p>
    </div>
  );
};

export default ApiLinkExamplePanel;
